import axios from 'axios';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import { isCaptchaEnabled } from '../services/captcha';
import { getFormRenderToken } from '../services/anti-bot';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    handleCloseDropdownOnBlur,
    setCaptchaEuScript,
} from '../utils';

const ADDRESS_REMOVED_COUNTRIES = ['mgMotorsIceland'];
const COMPANY_BUSINESS_ONLY_COUNTRIES = ['mgMotorsAustria', 'mgMotorsIceland'];

Vue.component('contact-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        stores: {
            value: Array,
        },
        brandstoreResultAmount: {
            value: Number,
        },
        sharpspringEndpointId: {
            value: String,
        },
        captchaEuPublicKey: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            requestType: 'private',
            requestIndex: 0,
            countryHandle: this.siteHandle !== 'default' ? this.siteHandle : '',
            internalCountryHandle: '',
            frBrandStoreHandle: '',
            selected: '',
            selectedBrandStore: '',
            selectedFrBrandStore: '',

            frBrandStores: {
                martinique: 'MG Martinique (97232 Le Lamentin)',
                guadeloupe: 'MG Guadeloupe (97122 Baie-Mahault)',
                guyane: 'MG Guyane (97324 Cayenne Cedex)',
                mayotte: 'MG Mayotte (97 600 Mamoudzou)',
            },

            dropdownCountry: null,
            dropdownSubject: null,
            dropdownFrStore: '',
            dropdownFrStoreValue: '',
            selectedTopic: '',

            // Brandstore search visibility
            isBrandstoreSearchVisible: false,
            isFranceCarDealerVisible: false,
            isGermanyCarDealerVisible: false,

            isNumberValid: true,
            isNoBrandstoreSelected: false,
            isVinNumberVisible: false,
            isCeeCountrySelected: false,
            disableFranceContactFormField: false,

            ceeCountries: [
                'Hungary',
                'Croatia',
                'Slovenia',
                'Serbia',
                'Czech Republic',
                'Slovakia',
                'North Macedonia',
                'Poland',
                'Romania',
                'Greece',
                'Montenegro',
                'Bosnia and Herzegovina',
                'Albania',
                'Kosovo',
            ],
            vinNumberVisibleCountries: [
                'mgMotorsBelgiumNl',
                'mgMotorsBelgiumFr',
                'mgMotorsLuxembourgFr',
                'mgMotorsPortugal',
                'mgMotorsPoland',
            ],

            newsletterSignUp: false,
        };
    },

    created() {
        const form = document.getElementById('contactForm');
        if (!form) return;

        form.reset();
    },

    computed: {
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },
        isCompanyInputVisible: function () {
            return this.requestType === 'business';
        },
        isAddressRemoved: function () {
            return ADDRESS_REMOVED_COUNTRIES.includes(this.countryHandle);
        },
        isCompanyBusinessOnly: function () {
            return COMPANY_BUSINESS_ONLY_COUNTRIES.includes(this.countryHandle);
        },
        isSubmitLoading: function () {
            return this.submitting ? 'button--loading' : '';
        },
        isSwitchButtonVisible: function () {
            // Private-only markets hide the Private/Business toggle (GENN-2776)
            const notAvailableCountries = ['mgMotorsMalta'];
            return !notAvailableCountries.includes(this.siteHandle);
        },
    },

    watch: {
        countryHandle() {
            this.showPlanNl = false;
            this.selectedBrandStore = null;
            this.dropdownStore = null;
            this.handleCompanyNameRequiredLabel(this.requestType);

            if (this.requestType === 'private' && this.isCompanyBusinessOnly) {
                this.clearCompanyField();
            }
        },

        selected(value) {
            this.isBrandstoreSearchVisible =
                this.handleToggleBrandstoreSearchVisibility(value);
            this.isFranceCarDealerVisible =
                this.siteHandle === 'mgMotorsFrance' &&
                value === 'want-contact-from-dealer';
            this.isGermanyCarDealerVisible =
                this.siteHandle === 'mgMotorsGermany';

            if (
                this.siteHandle === 'default' &&
                this.ceeCountries.includes(this.dropdownCountry)
            ) {
                this.isVinNumberVisible = [
                    'aftersales-service',
                    'accessories',
                ].includes(value);
            }

            if (this.vinNumberVisibleCountries.includes(this.siteHandle)) {
                this.isVinNumberVisible = ['aftersales-service'].includes(
                    value,
                );
            }

            // Croatia (GENN-2989): optional VIN shown only for "Pribor i oprema".
            if (this.siteHandle === 'mgMotorsCroatia') {
                this.isVinNumberVisible = value === 'accessories';
            }
        },
        requestType(requestTypeValue) {
            this.handleCompanyNameRequiredLabel(requestTypeValue);
            this.$nextTick(() => this.handleAddressRequiredLabels(requestTypeValue));

            if (requestTypeValue === 'private' && this.isCompanyBusinessOnly) {
                this.clearCompanyField();
            }
        },
        dropdownCountry() {
            if (
                this.siteHandle == 'default' &&
                this.dropdownCountry == 'France'
            ) {
                this.disableFranceContactFormField = true;
                return;
            }
            this.disableFranceContactFormField = false;
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseDropdownOnBlur('.new-input__dropdown-container--contact');

        this.handleAddressRequiredLabels(this.requestType);
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        checkIfSubmitDisabled() {
            return this.submitting || this.disableFranceContactFormField;
        },
        handleIsBrandstoreSelected() {
            this.isNoBrandstoreSelected = !this.isNoBrandstoreSelected;
        },

        clearCompanyField() {
            const companyField = document.getElementById('company');
            if (!companyField) return;

            companyField.value = '';
            companyField.dispatchEvent(new Event('input'));
        },

        handleCompanyNameRequiredLabel(requestTypeValue) {
            const countriesWithRequiredCompanyInput = [
                'mgMotorsFrance',
                'mgMotorsSweden',
                'mgMotorsLithuania',
                'mgMotorsAustria',
                'mgMotorsIreland',
                'mgMotorsIceland',
                'mgMotorsSwitzerlandDe',
                'mgMotorsSwitzerlandFr',
                'mgMotorsSwitzerlandIt',
            ];

            const isRequiredLabelStatic =
                this.siteHandle !== 'default' &&
                countriesWithRequiredCompanyInput.includes(this.countryHandle);

            if (isRequiredLabelStatic) return;

            const labelCompany = document.getElementById('label-company');
            if (!labelCompany) return;

            const companyField = document.getElementById('company');
            const labelText = labelCompany.innerText.replace(' *', '');
            const isRequired = requestTypeValue === 'business';

            labelCompany.innerText = isRequired ? `${labelText} *` : labelText;

            if (isRequired) {
                companyField?.setAttribute('aria-required', 'true');
                return;
            }

            companyField?.removeAttribute('aria-required');
        },

        handleAddressRequiredLabels(requestTypeValue) {
            const isBusinessType = requestTypeValue === 'business';

            document
                .querySelectorAll('.new-input__label--business-required')
                .forEach((span) => {
                    span.style.display = isBusinessType ? 'inline' : 'none';
                });

            document
                .querySelectorAll('[data-gdpr-business-required]')
                .forEach((input) => {
                    if (isBusinessType) {
                        input.setAttribute('aria-required', 'true');
                    } else {
                        input.removeAttribute('aria-required');
                    }
                });
        },

        handleToggleButtonActiveClass(index) {
            return this.requestIndex === index ? 'checked' : '';
        },

        handleToggleBrandstoreSearchVisibility(value) {
            const isShowOnSweden =
                this.countryHandle == 'mgMotorsSweden' &&
                value !== 'other-requests';

            const isShownForNoValue =
                this.countryHandle === 'mgMotorsNorway' ||
                this.countryHandle === 'mgMotorsNetherlands' ||
                this.countryHandle === 'mgMotorsSpain' ||
                this.countryHandle === 'mgMotorsLithuania';

            const isShownOnFrance =
                this.countryHandle === 'mgMotorsFrance' &&
                (value === 'aftersales-service' || value === 'accessories');

            const isShownOnPortugal =
                this.countryHandle === 'mgMotorsPortugal' &&
                value !== 'marketing-advertising';

            const isShownOnFinland = this.countryHandle === 'mgMotorsFinland';

            const isShownOnBelux =
                this.countryHandle === 'mgMotorsBelgiumNl' ||
                this.countryHandle === 'mgMotorsBelgiumFr' ||
                this.countryHandle === 'mgMotorsLuxembourgFr';

            return (
                (value.length && value === 'want-contact-from-dealer') ||
                isShowOnSweden ||
                isShownOnPortugal ||
                isShownForNoValue ||
                isShownOnFrance ||
                isShownOnFinland ||
                isShownOnBelux
            );
        },

        handleMarketingConsentChange(e) {
            e.preventDefault();
            const isConsentChecked = e.target.checked;
            this.newsletterSignUp = isConsentChecked;
            const hiddenMarketingInput = document.querySelector('#marketing');
            if (!hiddenMarketingInput) return;
            hiddenMarketingInput.setAttribute(
                'value',
                isConsentChecked ? '1' : '0',
            );
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let formData = new FormData(form);
            const data = {};

            for (let [key, val] of formData.entries()) {
                Object.assign(data, { [key]: val });
            }

            if (this.submitting) return;

            this.submitting = true;

            const hasSelectedTopic = [
                'mgMotorsSweden',
                'mgMotorsFinland',
            ].includes(this.siteHandle);

            const contactDataLayer = {
                event: 'contact',
                brandStore: this.selectedBrandStore
                    ? this.selectedBrandStore.title
                    : '',
                user_data: {
                    email_address: data.emailAddress,
                    phone_number: data.phone,
                    address: {
                        first_name: data.firstName,
                        last_name: data.lastName,
                        city: data.city,
                        postal_code: data.postalCode,
                    },
                },
                ...(hasSelectedTopic && {
                    topic: this.selectedTopic,
                }),
            };

            const contactFormEvent = hasSelectedTopic
                ? { event: 'contact_form', contact_subject: this.selectedTopic }
                : null;

            if (!this.isNumberValid) {
                this.submitting = false;
                return;
            }

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit(
                        form,
                        contactDataLayer,
                        null,
                        contactFormEvent,
                    );
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(
                    form,
                    contactDataLayer,
                    captchaSolution,
                    contactFormEvent,
                );
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        async handleDataSubmit(
            form,
            contactDataLayer,
            captchaSolution,
            contactFormEvent,
        ) {
            try {
                const formData = new FormData(form);
                formData.append('sourceUrl', window.location.href);
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }
                formData.append('formRenderToken', this.formRenderToken);
                const { data } = await axios.post('/', formData);
                const result = data;

                if (result.data.errors) {
                    this.submitting = false;
                    this.errors.clear();
                    this.errors.record(result.data.errors);

                    this.scrollToFirstError();
                    return;
                }

                this.errors.clear();
                this.isNumberValid = true;

                // Only Benelux countries
                if (
                    this.siteHandle === 'mgMotorsBelgiumNl' ||
                    this.siteHandle === 'mgMotorsBelgiumFr'
                ) {
                    window.__ss_noform.push([
                        'submit',
                        null,
                        this.sharpspringEndpointId,
                    ]);
                }

                this.sendGTMData(contactDataLayer, contactFormEvent);
                window.location = this.$refs.redirectUrl.value;
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData(extraDataToPush, contactFormEvent) {
            const country =
                this.siteHandle == 'default'
                    ? 'mgMotorsEurope'
                    : this.siteHandle;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                if (extraDataToPush) {
                    window.dataLayer.push(extraDataToPush);
                }

                if (contactFormEvent) {
                    window.dataLayer.push(contactFormEvent);
                }

                window.dataLayer.push({
                    event: 'contacts',
                    model: ' ',
                    edition: ' ',
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                });
            }
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;

            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        handleSetCountryHandleByDropdown(targetValue) {
            const frDropdownCountry = [
                'guyane',
                'guadeloupe',
                'martinique',
                'mayotte',
                'reunion',
            ];
            const isTargetValueFrCountry =
                frDropdownCountry.includes(targetValue);

            if (isTargetValueFrCountry) {
                this.countryHandle = 'mgMotorsFrance';
                return;
            }

            this.countryHandle = targetValue;
        },

        setDropdownCountry(event) {
            event.preventDefault();
            this.toggleDropdown(event);

            this.dropdownCountry = event.currentTarget.dataset.text;
            this.countryHandle = event.currentTarget.dataset.value;
            this.internalCountryHandle = event.currentTarget.dataset.dorm;

            this.dropdownSubject = null;
            this.isVinNumberVisible = false;

            this.handleSetCountryHandleByDropdown(
                event.currentTarget.dataset.value,
            );

            const territory = event.currentTarget.dataset.dorm;
            if (
                ['guyane', 'guadeloupe', 'martinique', 'mayotte'].includes(
                    territory,
                )
            ) {
                this.internalCountryHandle = territory;
                this.frBrandStoreHandle = territory;
                this.dropdownFrStore = JSON.stringify({
                    title: this.frBrandStores[territory],
                });

                this.dropdownFrStoreValue = this.frBrandStores[territory];

                return;
            }

            if (territory === 'reunion') {
                this.internalCountryHandle = territory;
                this.frBrandStoreHandle = 'MG Saint-Pierre (97 410)';
                this.dropdownFrStore = JSON.stringify({
                    title: 'MG Saint-Pierre (97 410)',
                });
                this.dropdownFrStoreValue = 'MG Saint-Pierre (97 410)';
            }

            if (territory === 'france') {
                this.dropdownFrStore = '';
                this.dropdownFrStoreValue = '';
            }

            if (this.ceeCountries.includes(this.dropdownCountry)) {
                this.isCeeCountrySelected = true;
            }
        },

        setDropdownFrBrandStore(event) {
            event.preventDefault();
            const text = event.currentTarget.dataset.text;
            this.dropdownFrStore = JSON.stringify({ title: text });
            this.dropdownFrStoreValue = text;

            this.frBrandStoreHandle = event.currentTarget.dataset.value;

            this.toggleDropdown(event);
        },

        setDropdownSubject(event) {
            event.preventDefault();
            this.dropdownSubject = event.currentTarget.dataset.text;
            this.selectedTopic = event.currentTarget.dataset.topic ?? '';
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.selected = value;
        },

        showStreetNameField() {
            return (
                this.countryHandle !== 'mgMotorsLithuania' ||
                this.requestType === 'business'
            );
        },

        showHouseNumberField() {
            return !['mgMotorsIreland', 'mgMotorsLithuania'].includes(
                this.countryHandle,
            );
        },

        showHouseNumberAdditionField() {
            if (this.countryHandle === 'mgMotorsIreland') return false;

            return this.countryHandle !== 'mgMotorsLithuania';
        },

        showPostalCodeField() {
            const beluxHandles = [
                'mgMotorsBelgiumNl',
                'mgMotorsBelgiumFr',
                'mgMotorsLuxembourgFr',
            ];
            if (beluxHandles.includes(this.countryHandle)) return false;

            return (
                this.countryHandle !== 'mgMotorsLithuania' ||
                this.requestType === 'business'
            );
        },

        showCityField() {
            return (
                this.countryHandle !== 'mgMotorsLithuania' ||
                this.requestType === 'private'
            );
        },
    },
});
